import { isToolLifecycleItemType, } from "@t3tools/contracts";
export function toPayloadRecord(payload) {
    return payload && typeof payload === "object" ? payload : null;
}
function requestKindFromRequestType(requestType) {
    switch (requestType) {
        case "command_execution_approval":
        case "exec_command_approval":
        case "dynamic_tool_call":
            return "command";
        case "file_read_approval":
            return "file-read";
        case "file_change_approval":
        case "apply_patch_approval":
            return "file-change";
        default:
            return null;
    }
}
export function isServiceActivity(activity) {
    return (activity.kind === "runtime.warning" ||
        activity.kind === "runtime.error" ||
        (activity.kind.startsWith("provider.") && activity.kind.endsWith(".failed")) ||
        (activity.kind.startsWith("tool.") && activity.public?.audience === "developer"));
}
export class WorkLogPrefix {
    tasks = new Map();
    compactions = new Map();
    plans = new Set();
    pending = new Map();
    fallbackPending = new Map();
    rows = new Map();
    rendered = [];
    nextOrder = 0;
    dependentIds = new Set();
    dependentKeys = new Set();
    dependentFallbacks = new Set();
    consumed = new Map();
    consumedFallbacks = new Map();
    projectedLast;
    consume(entry) {
        const key = lifecyclePairKey(entry);
        this.consumed.set(key, (this.consumed.get(key) ?? 0) + 1);
        if (entry.toolCallId === undefined && entry.labelKey === undefined) {
            const fallback = entry.itemType ?? "";
            this.consumedFallbacks.set(fallback, (this.consumedFallbacks.get(fallback) ?? 0) + 1);
        }
    }
    trackTail(result) {
        this.consumed.clear();
        this.consumedFallbacks.clear();
        const remaining = new Set(result.pending.map((entry) => entry.id));
        for (const entry of result.seededPending) {
            if (!remaining.has(entry.id) && this.pendingRows(lifecyclePairKey(entry))?.has(entry.id))
                this.consume(entry);
        }
    }
    trackDependencies(result, isPrefix) {
        this.projectedLast = result.rendered.findLast(isPrefix);
        this.dependentIds = new Set(result.rendered.filter(isPrefix).map((entry) => entry.id));
        this.consumed.clear();
        this.consumedFallbacks.clear();
        const remaining = new Set(result.pending.map((entry) => entry.id));
        for (const rows of this.pending.values()) {
            for (const { entry } of rows.values())
                if (!remaining.has(entry.id))
                    this.consume(entry);
        }
        this.dependentKeys.clear();
        this.dependentFallbacks.clear();
        for (const entry of result.pending) {
            if (!this.dependentIds.has(entry.id))
                continue;
            this.dependentKeys.add(lifecyclePairKey(entry));
            if (entry.toolCallId === undefined && entry.labelKey === undefined)
                this.dependentFallbacks.add(entry.itemType ?? "");
        }
    }
    pendingRows(key, fallback = false) {
        return (fallback ? this.fallbackPending : this.pending).get(key);
    }
    addPending(row) {
        const add = (target, key) => {
            let rows = target.get(key);
            if (!rows)
                target.set(key, (rows = new Map()));
            rows.set(row.entry.id, row);
        };
        add(this.pending, lifecyclePairKey(row.entry));
        if (row.entry.toolCallId === undefined && row.entry.labelKey === undefined) {
            add(this.fallbackPending, row.entry.itemType ?? "");
        }
    }
    removePending(entry) {
        this.pending.get(lifecyclePairKey(entry))?.delete(entry.id);
        this.fallbackPending.get(entry.itemType ?? "")?.delete(entry.id);
    }
    touches(activity) {
        const entry = toDerivedWorkLogEntry(activity, "ru");
        return (this.dependentIds.has(activity.id) ||
            (entry.taskId !== undefined && this.tasks.has(entry.taskId)) ||
            (entry.compactionKey !== undefined && this.compactions.has(entry.compactionKey)) ||
            ((activity.kind === "tool.updated" || activity.kind === "tool.completed") &&
                ((this.pendingRows(lifecyclePairKey(entry))?.size ?? 0) >
                    (this.consumed.get(lifecyclePairKey(entry)) ?? 0) ||
                    this.dependentKeys.has(lifecyclePairKey(entry)) ||
                    (entry.toolCallId === undefined &&
                        entry.labelKey === undefined &&
                        ((this.pendingRows(entry.itemType ?? "", true)?.size ?? 0) >
                            (this.consumedFallbacks.get(entry.itemType ?? "") ?? 0) ||
                            this.dependentFallbacks.has(entry.itemType ?? ""))) ||
                    (this.projectedLast !== undefined &&
                        shouldCollapseToolLifecycleEntries(this.projectedLast, entry)))));
    }
    seed(activities) {
        const selected = new Map();
        const needed = new Map();
        for (const activity of activities) {
            const entry = toDerivedWorkLogEntry(activity, "ru");
            const task = entry.taskId === undefined ? undefined : this.tasks.get(entry.taskId);
            const compaction = entry.compactionKey === undefined ? undefined : this.compactions.get(entry.compactionKey);
            if (task)
                selected.set(task.entry.id, task);
            if (compaction)
                selected.set(compaction.entry.id, compaction);
            if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed")
                continue;
            const exact = this.pendingRows(lifecyclePairKey(entry));
            const fallback = entry.toolCallId === undefined && entry.labelKey === undefined
                ? this.pendingRows(entry.itemType ?? "", true)
                : undefined;
            for (const rows of [exact, fallback]) {
                if (rows)
                    needed.set(rows, (needed.get(rows) ?? 0) + 1);
            }
        }
        for (const [rows, count] of needed) {
            let remaining = count;
            for (const [id, row] of rows) {
                selected.set(id, row);
                if (--remaining === 0)
                    break;
            }
        }
        const preceding = this.rendered.slice(-activities.length - 1);
        for (const entry of preceding) {
            const row = this.rows.get(entry.id);
            if (row)
                selected.set(entry.id, row);
        }
        const rows = [...selected.values()].toSorted((a, b) => a.order - b.order);
        return { rows, preceding };
    }
    commit(result) {
        this.consumed.clear();
        this.consumedFallbacks.clear();
        this.dependentIds.clear();
        this.dependentKeys.clear();
        this.dependentFallbacks.clear();
        for (const entry of result.seededPending)
            this.removePending(entry);
        for (const entry of result.stateEntries) {
            const previous = this.rows.get(entry.id);
            this.rows.set(entry.id, { entry, order: previous?.order ?? this.nextOrder++ });
        }
        for (const [id, index] of result.tasks) {
            const entry = result.stateEntries[index];
            if (entry)
                this.tasks.set(id, this.rows.get(entry.id));
        }
        for (const [id, index] of result.compactions) {
            const entry = result.stateEntries[index];
            if (entry)
                this.compactions.set(id, this.rows.get(entry.id));
        }
        for (const id of result.plans)
            this.plans.add(id);
        for (const entry of result.pending)
            this.addPending(this.rows.get(entry.id));
        this.rendered.splice(this.rendered.length - result.precedingCount, result.precedingCount, ...result.rendered);
        this.projectedLast = this.rendered.at(-1);
    }
}
export function deriveWorkLogEntries(activities, latestTurnId, latestTurnSettled = false, locale = "ru", includeServiceActivities = false) {
    return deriveWorkLogSegment(activities, latestTurnId, latestTurnSettled, locale, includeServiceActivities).entries;
}
export function deriveWorkLogSegment(activities, latestTurnId, latestTurnSettled = false, locale = "ru", includeServiceActivities = false, prefix) {
    const shown = includeServiceActivities
        ? activities
        : activities.filter((activity) => !isServiceActivity(activity));
    const ordered = [...shown].toSorted(compareActivitiesByOrder);
    const seed = prefix?.seed(ordered);
    const entries = seed?.rows.map((row) => row.entry) ?? [];
    const seededIds = new Set(entries.map((entry) => entry.id));
    const taskRowIndexById = new Map();
    const compactionRowIndexByKey = new Map();
    const planTaskIds = new Set();
    const liveTurnId = !latestTurnSettled && latestTurnId !== undefined ? latestTurnId : null;
    const pendingStarts = entries.filter((entry) => entry.activityKind === "tool.started" ||
        (entry.activityKind === "tool.updated" && entry.active === true));
    const seededPending = [...pendingStarts];
    entries.forEach((entry, index) => {
        if (entry.taskId && prefix?.tasks.get(entry.taskId)?.entry.id === entry.id)
            taskRowIndexById.set(entry.taskId, index);
        if (entry.compactionKey && prefix?.compactions.get(entry.compactionKey)?.entry.id === entry.id)
            compactionRowIndexByKey.set(entry.compactionKey, index);
    });
    for (const activity of ordered) {
        const isCompaction = activity.kind === "context-compaction";
        if (activity.kind === "context-window.updated" || activity.kind === "turn.failed")
            continue;
        if (isPlanBoundaryToolActivity(activity))
            continue;
        const isLiveTurnActivity = liveTurnId !== null && activity.turnId === liveTurnId;
        if (activity.kind === "tool.started") {
            if (!isLiveTurnActivity)
                continue;
            const started = toDerivedWorkLogEntry(activity, locale);
            if (started.itemType === "collab_agent_tool_call" && started.heartbeat === undefined) {
                continue;
            }
            started.active = true;
            pendingStarts.push(started);
            entries.push(started);
            continue;
        }
        const entry = toDerivedWorkLogEntry(activity, locale);
        if (activity.kind === "tool.updated" || activity.kind === "tool.completed") {
            const started = takePendingStart(pendingStarts, entry);
            if (started) {
                const startedIndex = entries.indexOf(started);
                if (startedIndex !== -1) {
                    entries.splice(startedIndex, 1);
                }
                entry.startedAt ??= started.startedAt ?? started.createdAt;
            }
            if (activity.kind === "tool.updated" && isLiveTurnActivity) {
                entry.active = true;
                pendingStarts.push(entry);
            }
        }
        // Compaction rows survive across turns; started+completed lifecycle
        // activities of one compaction collapse into a single row that updates
        // in place, anchored at the started activity's timeline position.
        if (isCompaction && entry.compactionKey !== undefined) {
            const existingIndex = compactionRowIndexByKey.get(entry.compactionKey);
            const existing = existingIndex !== undefined ? entries[existingIndex] : undefined;
            if (existing && existingIndex !== undefined) {
                entries[existingIndex] = applyCompactionUpdate(existing, entry);
                continue;
            }
            compactionRowIndexByKey.set(entry.compactionKey, entries.length);
            entries.push(entry);
            continue;
        }
        // Plan-mode tasks (and degenerate task rows without a task id) keep the
        // legacy stream of individual rows; only sub-agent tasks collapse into a
        // single status row that updates in place: running → working → finished.
        if (entry.taskType === "plan" && entry.taskId) {
            planTaskIds.add(entry.taskId);
        }
        const isSubAgentTaskRow = entry.taskId !== undefined &&
            entry.taskType !== "plan" &&
            !planTaskIds.has(entry.taskId) &&
            !prefix?.plans.has(entry.taskId);
        if (entry.taskStatus && !isSubAgentTaskRow) {
            delete entry.taskStatus;
            delete entry.taskId;
        }
        if (entry.activityKind === "task.started" && !entry.taskStatus)
            continue;
        // Collab tool-call markers (Claude Task items, Codex spawn/wait calls)
        // duplicate what the task status row already shows; the wait marker stays
        // because it drives the "waiting for helpers" state.
        if (entry.itemType === "collab_agent_tool_call" && entry.heartbeat === undefined) {
            continue;
        }
        if (entry.taskId && entry.taskStatus) {
            const existingIndex = taskRowIndexById.get(entry.taskId);
            const existing = existingIndex !== undefined ? entries[existingIndex] : undefined;
            if (existing && existingIndex !== undefined) {
                entries[existingIndex] = applyTaskStatusUpdate(existing, entry);
                continue;
            }
            taskRowIndexById.set(entry.taskId, entries.length);
        }
        entries.push(entry);
    }
    // A "Compacting context" row without a completion is only meaningful while
    // its turn is still running; otherwise the session died mid-compaction and
    // the dangling row would spin forever.
    const remaining = new Map(entries.map((entry) => [entry.id, entry]));
    const displayEntries = [
        ...(seed?.preceding.flatMap((entry) => seededIds.has(entry.id)
            ? remaining.has(entry.id)
                ? [remaining.get(entry.id)]
                : []
            : [entry]) ?? []),
        ...entries.filter((entry) => !seededIds.has(entry.id)),
    ];
    const visible = displayEntries.filter((entry) => entry.compactionPhase !== "started" ||
        (!latestTurnSettled && latestTurnId !== undefined && entry.turnId === latestTurnId));
    const rendered = collapseDerivedWorkLogEntries(visible);
    const collapsed = rendered.map(({ activityKind: _activityKind, collapseKey: _collapseKey, toolCallId: _toolCallId, taskType: _taskType, compactionKey: _compactionKey, ...entry }) => entry);
    return {
        entries: collapsed,
        rendered,
        stateEntries: entries,
        tasks: taskRowIndexById,
        compactions: compactionRowIndexByKey,
        plans: planTaskIds,
        pending: pendingStarts.filter((entry) => entries.includes(entry)),
        seededPending,
        precedingCount: seed?.preceding.length ?? 0,
    };
}
function lifecyclePairKey(entry) {
    if (entry.toolCallId) {
        return `call:${entry.toolCallId}`;
    }
    if (entry.labelKey !== undefined) {
        return `label:${entry.labelKey}${entry.label}`;
    }
    return `type:${entry.itemType ?? ""}${normalizeCompactToolLabel(entry.label)}`;
}
function takePendingStart(pending, entry) {
    const key = lifecyclePairKey(entry);
    let index = pending.findIndex((candidate) => lifecyclePairKey(candidate) === key);
    if (index === -1 && entry.toolCallId === undefined && entry.labelKey === undefined) {
        index = pending.findIndex((candidate) => candidate.toolCallId === undefined &&
            candidate.labelKey === undefined &&
            (candidate.itemType ?? "") === (entry.itemType ?? ""));
    }
    if (index === -1) {
        return null;
    }
    return pending.splice(index, 1)[0] ?? null;
}
function localizedLabelText(text, locale) {
    const localized = text[locale]?.trim();
    return localized && localized.length > 0 ? localized : text.ru.trim();
}
export function deriveWorkLogRawFields(payload, heading = "") {
    const record = toPayloadRecord(payload);
    const fields = {};
    if (!record) {
        return fields;
    }
    const title = extractToolTitle(record);
    const commandPreview = extractToolCommand(record);
    const changedFiles = extractChangedFiles(record);
    const detail = extractToolDetail(record, title ?? heading);
    const itemType = extractWorkLogItemType(record);
    const item = asRecord(asRecord(record.data)?.item);
    const exitCode = asNumber(item?.exitCode) ??
        stripTrailingExitCode(asTrimmedString(record.detail) ?? "").exitCode;
    const durationMs = asNumber(record.durationMs) ?? asNumber(item?.durationMs);
    const mcpServer = asTrimmedString(item?.server);
    const mcpTool = asTrimmedString(item?.tool);
    const mcpArguments = item?.arguments === undefined ? null : formatJsonPreview(item.arguments);
    const viewPath = itemType === "image_view" ? asTrimmedString(item?.path) : null;
    const query = itemType === "web_search" ? asTrimmedString(item?.query) : null;
    const output = asTrimmedString(item?.aggregatedOutput) ??
        (item?.result === undefined ? null : formatJsonPreview(item.result)) ??
        extractFileChangeDiff(item);
    if (detail)
        fields.detail = detail;
    if (commandPreview.command)
        fields.command = commandPreview.command;
    if (commandPreview.rawCommand)
        fields.rawCommand = commandPreview.rawCommand;
    if (changedFiles.length > 0 && itemType !== "image_view")
        fields.changedFiles = changedFiles;
    if (title)
        fields.toolTitle = title;
    if (itemType)
        fields.itemType = itemType;
    if (exitCode !== undefined)
        fields.exitCode = exitCode;
    if (durationMs !== null)
        fields.durationMs = durationMs;
    if (mcpServer)
        fields.mcpServer = mcpServer;
    if (mcpTool)
        fields.mcpTool = mcpTool;
    if (mcpArguments)
        fields.mcpArguments = mcpArguments;
    if (output)
        fields.output = output;
    if (query)
        fields.query = query;
    if (viewPath)
        fields.viewPath = viewPath;
    return fields;
}
function formatJsonPreview(value) {
    if (typeof value === "string") {
        return value.trim().length > 0 ? value : null;
    }
    try {
        return JSON.stringify(value, null, 2);
    }
    catch {
        return null;
    }
}
function applyTaskStatusUpdate(previous, next) {
    const isSettled = previous.taskStatus === "finished" || previous.taskStatus === "failed";
    if (isSettled && next.taskStatus === "running") {
        return previous;
    }
    const { detail: _detail, ...rest } = previous;
    return {
        ...rest,
        tone: next.tone,
        activityKind: next.activityKind,
        ...(next.taskStatus ? { taskStatus: next.taskStatus } : {}),
    };
}
// A completed compaction is terminal: a late `started` activity replayed out of
// order must not put the row back into its pulsing in-progress state.
function applyCompactionUpdate(previous, next) {
    if (previous.compactionPhase === "completed") {
        return previous;
    }
    return {
        ...previous,
        label: next.label,
        tone: next.tone,
        activityKind: next.activityKind,
        ...(next.compactionPhase ? { compactionPhase: next.compactionPhase } : {}),
        ...(next.detail ? { detail: next.detail } : {}),
    };
}
function isPlanBoundaryToolActivity(activity) {
    if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed") {
        return false;
    }
    const payload = toPayloadRecord(activity.payload);
    return typeof payload?.detail === "string" && payload.detail.startsWith("ExitPlanMode:");
}
function toDerivedWorkLogEntry(activity, locale) {
    const payload = toPayloadRecord(activity.payload);
    const isTaskActivity = activity.kind === "task.started" ||
        activity.kind === "task.progress" ||
        activity.kind === "task.completed";
    const publicLabel = activity.kind.startsWith("tool.") && activity.public !== undefined ? activity.public : null;
    const raw = payload === null || isTaskActivity ? null : deriveWorkLogRawFields(payload, activity.summary);
    const taskStatus = activity.kind === "task.started"
        ? "running"
        : activity.kind === "task.progress"
            ? "working"
            : activity.kind === "task.completed"
                ? activity.tone === "error"
                    ? "failed"
                    : "finished"
                : undefined;
    const taskSummary = isTaskActivity && typeof payload?.summary === "string" && payload.summary.length > 0
        ? payload.summary
        : null;
    const taskDetailAsLabel = isTaskActivity &&
        !taskSummary &&
        typeof payload?.detail === "string" &&
        payload.detail.length > 0
        ? payload.detail
        : null;
    const taskLabel = taskSummary || taskDetailAsLabel;
    const detail = isTaskActivity
        ? !taskDetailAsLabel &&
            payload &&
            typeof payload.detail === "string" &&
            payload.detail.length > 0
            ? stripTrailingExitCode(payload.detail).output
            : null
        : (raw?.detail ?? null);
    const toolCallId = isTaskActivity ? null : extractToolCallId(payload);
    const entry = {
        id: activity.id,
        createdAt: activity.createdAt,
        label: publicLabel
            ? localizedLabelText(publicLabel.label.text, locale)
            : taskLabel || activity.summary,
        tone: taskStatus === "running" || taskStatus === "working"
            ? "thinking"
            : activity.tone === "approval"
                ? "info"
                : activity.tone,
        activityKind: activity.kind,
        turnId: activity.turnId,
    };
    if (activity.kind === "context-compaction") {
        entry.compactionPhase = payload?.phase === "started" ? "started" : "completed";
        // Providers key the compaction lifecycle by item id; histories predating
        // that fall back to the turn so a started/completed pair still collapses.
        entry.compactionKey =
            typeof payload?.itemId === "string" && payload.itemId.length > 0
                ? payload.itemId
                : (activity.turnId ?? activity.id);
        if (entry.compactionPhase === "started") {
            entry.tone = "thinking";
        }
    }
    if (isTaskActivity) {
        if (typeof payload?.taskId === "string" && payload.taskId.length > 0) {
            entry.taskId = payload.taskId;
        }
        if (taskStatus) {
            entry.taskStatus = taskStatus;
        }
        if (typeof payload?.taskType === "string") {
            entry.taskType = payload.taskType;
        }
        if (typeof payload?.childThreadId === "string" && payload.childThreadId.length > 0) {
            entry.childThreadId = payload.childThreadId;
        }
        if (typeof payload?.prompt === "string" && payload.prompt.length > 0) {
            entry.taskPrompt = payload.prompt;
        }
        if (typeof payload?.lastToolName === "string" && payload.lastToolName.length > 0) {
            entry.lastToolName = payload.lastToolName;
        }
    }
    if (publicLabel) {
        entry.labelKey = publicLabel.label.key;
        if (Object.keys(publicLabel.params).length > 0) {
            entry.labelParams = publicLabel.params;
        }
        entry.audience = publicLabel.audience;
        if (publicLabel.heartbeat !== undefined) {
            entry.heartbeat = publicLabel.heartbeat;
        }
        if (publicLabel.unmatched) {
            entry.unmatched = true;
        }
        if (publicLabel.startedAt !== undefined) {
            entry.startedAt = publicLabel.startedAt;
        }
    }
    const itemType = extractWorkLogItemType(payload) ?? publicItemType(publicLabel);
    const requestKind = extractWorkLogRequestKind(payload);
    if (activity.sequence !== undefined) {
        entry.sequence = activity.sequence;
    }
    if (detail) {
        entry.detail = detail;
    }
    if (raw) {
        const { detail: _detail, itemType: _itemType, ...rawFields } = raw;
        Object.assign(entry, rawFields);
    }
    if (itemType) {
        entry.itemType = itemType;
    }
    if (requestKind) {
        entry.requestKind = requestKind;
    }
    if (toolCallId) {
        entry.toolCallId = toolCallId;
    }
    const collapseKey = deriveToolLifecycleCollapseKey(entry);
    if (collapseKey) {
        entry.collapseKey = collapseKey;
    }
    return entry;
}
function collapseDerivedWorkLogEntries(entries) {
    const collapsed = [];
    for (const entry of entries) {
        const previous = collapsed.at(-1);
        if (previous && shouldCollapseToolLifecycleEntries(previous, entry)) {
            collapsed[collapsed.length - 1] = mergeDerivedWorkLogEntries(previous, entry);
            continue;
        }
        collapsed.push(entry);
    }
    return collapsed;
}
function shouldCollapseToolLifecycleEntries(previous, next) {
    if (previous.activityKind !== "tool.updated" && previous.activityKind !== "tool.completed") {
        return false;
    }
    if (next.activityKind !== "tool.updated" && next.activityKind !== "tool.completed") {
        return false;
    }
    if (previous.activityKind === "tool.completed") {
        return false;
    }
    if (previous.collapseKey !== undefined && previous.collapseKey === next.collapseKey) {
        return true;
    }
    return (previous.toolCallId !== undefined &&
        next.toolCallId === undefined &&
        previous.itemType === next.itemType &&
        normalizeCompactToolLabel(previous.toolTitle ?? previous.label) ===
            normalizeCompactToolLabel(next.toolTitle ?? next.label));
}
function mergeDerivedWorkLogEntries(previous, next) {
    const changedFiles = mergeChangedFiles(previous.changedFiles, next.changedFiles);
    const detail = next.detail ?? previous.detail;
    const command = next.command ?? previous.command;
    const rawCommand = next.rawCommand ?? previous.rawCommand;
    const toolTitle = next.toolTitle ?? previous.toolTitle;
    const itemType = next.itemType ?? previous.itemType;
    const requestKind = next.requestKind ?? previous.requestKind;
    const collapseKey = next.collapseKey ?? previous.collapseKey;
    const toolCallId = next.toolCallId ?? previous.toolCallId;
    const startedAt = next.startedAt ?? previous.startedAt ?? previous.createdAt;
    // Anchor the collapsed entry to the earliest activity so it keeps its
    // original timeline position rather than jumping to the merged-in event.
    const sequence = previous.sequence ?? next.sequence;
    const { active: _active, ...previousRest } = previous;
    return {
        ...previousRest,
        ...next,
        ...(detail ? { detail } : {}),
        ...(command ? { command } : {}),
        ...(rawCommand ? { rawCommand } : {}),
        ...(changedFiles.length > 0 ? { changedFiles } : {}),
        ...(toolTitle ? { toolTitle } : {}),
        ...(itemType ? { itemType } : {}),
        ...(requestKind ? { requestKind } : {}),
        ...(collapseKey ? { collapseKey } : {}),
        ...(toolCallId ? { toolCallId } : {}),
        startedAt,
        ...(sequence !== undefined ? { sequence } : {}),
    };
}
function mergeChangedFiles(previous, next) {
    const merged = [...(previous ?? []), ...(next ?? [])];
    if (merged.length === 0) {
        return [];
    }
    return [...new Set(merged)];
}
function deriveToolLifecycleCollapseKey(entry) {
    if (entry.activityKind !== "tool.updated" && entry.activityKind !== "tool.completed") {
        return undefined;
    }
    if (entry.toolCallId) {
        return `tool:${entry.toolCallId}`;
    }
    const normalizedLabel = normalizeCompactToolLabel(entry.toolTitle ?? entry.label);
    const detail = entry.detail?.trim() ?? "";
    const itemType = entry.itemType ?? "";
    if (normalizedLabel.length === 0 && detail.length === 0 && itemType.length === 0) {
        return undefined;
    }
    return [itemType, normalizedLabel, detail].join("\u001f");
}
function normalizeCompactToolLabel(value) {
    return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}
function asRecord(value) {
    return value && typeof value === "object" ? value : null;
}
function asTrimmedString(value) {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}
function asNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function trimMatchingOuterQuotes(value) {
    const trimmed = value.trim();
    if ((trimmed.startsWith("'") && trimmed.endsWith("'")) ||
        (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
        const unquoted = trimmed.slice(1, -1).trim();
        return unquoted.length > 0 ? unquoted : trimmed;
    }
    return trimmed;
}
function executableBasename(value) {
    const trimmed = trimMatchingOuterQuotes(value);
    if (trimmed.length === 0) {
        return null;
    }
    const normalized = trimmed.replace(/\\/g, "/");
    const segments = normalized.split("/");
    const last = segments.at(-1)?.trim() ?? "";
    return last.length > 0 ? last.toLowerCase() : null;
}
function splitExecutableAndRest(value) {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    if (trimmed.startsWith('"') || trimmed.startsWith("'")) {
        const quote = trimmed.charAt(0);
        const closeIndex = trimmed.indexOf(quote, 1);
        if (closeIndex <= 0) {
            return null;
        }
        return {
            executable: trimmed.slice(0, closeIndex + 1),
            rest: trimmed.slice(closeIndex + 1).trim(),
        };
    }
    const firstWhitespace = trimmed.search(/\s/);
    if (firstWhitespace < 0) {
        return {
            executable: trimmed,
            rest: "",
        };
    }
    return {
        executable: trimmed.slice(0, firstWhitespace),
        rest: trimmed.slice(firstWhitespace).trim(),
    };
}
const SHELL_WRAPPER_SPECS = [
    {
        executables: ["pwsh", "pwsh.exe", "powershell", "powershell.exe"],
        wrapperFlagPattern: /(?:^|\s)-command\s+/i,
    },
    {
        executables: ["cmd", "cmd.exe"],
        wrapperFlagPattern: /(?:^|\s)\/c\s+/i,
    },
    {
        executables: ["bash", "sh", "zsh"],
        wrapperFlagPattern: /(?:^|\s)-(?:l)?c\s+/i,
    },
];
function findShellWrapperSpec(shell) {
    return SHELL_WRAPPER_SPECS.find((spec) => spec.executables.includes(shell));
}
function unwrapCommandRemainder(value, wrapperFlagPattern) {
    const match = wrapperFlagPattern.exec(value);
    if (!match) {
        return null;
    }
    const command = value.slice(match.index + match[0].length).trim();
    if (command.length === 0) {
        return null;
    }
    const unwrapped = trimMatchingOuterQuotes(command);
    return unwrapped.length > 0 ? unwrapped : null;
}
function unwrapKnownShellCommandWrapper(value) {
    const split = splitExecutableAndRest(value);
    if (!split || split.rest.length === 0) {
        return value;
    }
    const shell = executableBasename(split.executable);
    if (!shell) {
        return value;
    }
    const spec = findShellWrapperSpec(shell);
    if (!spec) {
        return value;
    }
    return unwrapCommandRemainder(split.rest, spec.wrapperFlagPattern) ?? value;
}
function formatCommandArrayPart(value) {
    return /[\s"'`]/.test(value) ? `"${value.replace(/"/g, '\\"')}"` : value;
}
function formatCommandValue(value) {
    const direct = asTrimmedString(value);
    if (direct) {
        return direct;
    }
    if (!Array.isArray(value)) {
        return null;
    }
    const parts = [];
    for (const entry of value) {
        const part = asTrimmedString(entry);
        if (part !== null) {
            parts.push(part);
        }
    }
    if (parts.length === 0) {
        return null;
    }
    return parts.map((part) => formatCommandArrayPart(part)).join(" ");
}
function normalizeCommandValue(value) {
    const formatted = formatCommandValue(value);
    return formatted ? unwrapKnownShellCommandWrapper(formatted) : null;
}
function toRawToolCommand(value, normalizedCommand) {
    const formatted = formatCommandValue(value);
    if (!formatted || normalizedCommand === null) {
        return null;
    }
    return formatted === normalizedCommand ? null : formatted;
}
function extractToolCommand(payload) {
    const data = asRecord(payload?.data);
    const item = asRecord(data?.item);
    const itemResult = asRecord(item?.result);
    const itemInput = asRecord(item?.input);
    const itemType = asTrimmedString(payload?.itemType);
    const detail = asTrimmedString(payload?.detail);
    const candidates = [
        item?.command,
        itemInput?.command,
        itemResult?.command,
        data?.command,
        itemType === "command_execution" && detail ? stripTrailingExitCode(detail).output : null,
    ];
    for (const candidate of candidates) {
        const command = normalizeCommandValue(candidate);
        if (!command) {
            continue;
        }
        return {
            command,
            rawCommand: toRawToolCommand(candidate, command),
        };
    }
    return {
        command: null,
        rawCommand: null,
    };
}
function extractToolTitle(payload) {
    return asTrimmedString(payload?.title);
}
function extractToolCallId(payload) {
    const data = asRecord(payload?.data);
    return asTrimmedString(data?.toolCallId);
}
function normalizeInlinePreview(value) {
    return value.replace(/\s+/g, " ").trim();
}
function truncateInlinePreview(value, maxLength = 84) {
    if (value.length <= maxLength) {
        return value;
    }
    return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}
function normalizePreviewForComparison(value) {
    const normalized = asTrimmedString(value);
    if (!normalized) {
        return null;
    }
    return normalizeCompactToolLabel(normalizeInlinePreview(normalized)).toLowerCase();
}
function summarizeToolTextOutput(value) {
    const lines = [];
    for (const rawLine of value.split(/\r?\n/u)) {
        const line = normalizeInlinePreview(rawLine);
        if (line.length > 0) {
            lines.push(line);
        }
    }
    const firstLine = lines.find((line) => line !== "```");
    if (firstLine) {
        return truncateInlinePreview(firstLine);
    }
    if (lines.length > 1) {
        return `${lines.length.toLocaleString()} lines`;
    }
    return null;
}
function summarizeToolRawOutput(payload) {
    const data = asRecord(payload?.data);
    const rawOutput = asRecord(data?.rawOutput);
    if (!rawOutput) {
        return null;
    }
    const totalFiles = asNumber(rawOutput.totalFiles);
    if (totalFiles !== null) {
        const suffix = rawOutput.truncated === true ? "+" : "";
        return `${totalFiles.toLocaleString()} file${totalFiles === 1 ? "" : "s"}${suffix}`;
    }
    const content = asTrimmedString(rawOutput.content);
    if (content) {
        return summarizeToolTextOutput(content);
    }
    const stdout = asTrimmedString(rawOutput.stdout);
    if (stdout) {
        return summarizeToolTextOutput(stdout);
    }
    return null;
}
function isCommandToolDetail(payload, heading) {
    const data = asRecord(payload?.data);
    const kind = asTrimmedString(data?.kind)?.toLowerCase();
    const title = asTrimmedString(payload?.title ?? heading)?.toLowerCase();
    return (extractWorkLogItemType(payload) === "command_execution" ||
        kind === "execute" ||
        title === "terminal" ||
        title === "ran command");
}
function extractToolDetail(payload, heading) {
    const rawDetail = asTrimmedString(payload?.detail);
    const detail = rawDetail ? stripTrailingExitCode(rawDetail).output : null;
    const normalizedHeading = normalizePreviewForComparison(heading);
    const normalizedDetail = normalizePreviewForComparison(detail);
    if (detail && normalizedHeading !== normalizedDetail) {
        return detail;
    }
    if (isCommandToolDetail(payload, heading)) {
        return null;
    }
    const rawOutputSummary = summarizeToolRawOutput(payload);
    if (rawOutputSummary) {
        const normalizedRawOutputSummary = normalizePreviewForComparison(rawOutputSummary);
        if (normalizedRawOutputSummary !== normalizedHeading) {
            return rawOutputSummary;
        }
    }
    return null;
}
function stripTrailingExitCode(value) {
    const trimmed = value.trim();
    const match = /^(?<output>[\s\S]*?)(?:\s*<exited with exit code (?<code>\d+)>)\s*$/i.exec(trimmed);
    if (!match?.groups) {
        return {
            output: trimmed.length > 0 ? trimmed : null,
        };
    }
    const exitCode = Number.parseInt(match.groups.code ?? "", 10);
    const normalizedOutput = match.groups.output?.trim() ?? "";
    return {
        output: normalizedOutput.length > 0 ? normalizedOutput : null,
        ...(Number.isInteger(exitCode) ? { exitCode } : {}),
    };
}
function extractFileChangeDiff(item) {
    if (!Array.isArray(item?.changes)) {
        return null;
    }
    const diffs = item.changes
        .map((change) => asTrimmedString(asRecord(change)?.diff))
        .filter((diff) => diff !== null);
    return diffs.length > 0 ? diffs.join("\n\n") : null;
}
function extractWorkLogItemType(payload) {
    if (typeof payload?.itemType === "string" && isToolLifecycleItemType(payload.itemType)) {
        return payload.itemType;
    }
    return undefined;
}
function publicItemType(publicLabel) {
    return publicLabel !== null && isToolLifecycleItemType(publicLabel.itemType)
        ? publicLabel.itemType
        : undefined;
}
function extractWorkLogRequestKind(payload) {
    if (payload?.requestKind === "command" ||
        payload?.requestKind === "file-read" ||
        payload?.requestKind === "file-change") {
        return payload.requestKind;
    }
    return requestKindFromRequestType(payload?.requestType) ?? undefined;
}
function pushChangedFile(target, seen, value) {
    const normalized = asTrimmedString(value);
    if (!normalized || seen.has(normalized)) {
        return;
    }
    seen.add(normalized);
    target.push(normalized);
}
function collectChangedFiles(value, target, seen, depth) {
    if (depth > 4 || target.length >= 12) {
        return;
    }
    if (Array.isArray(value)) {
        for (const entry of value) {
            collectChangedFiles(entry, target, seen, depth + 1);
            if (target.length >= 12) {
                return;
            }
        }
        return;
    }
    const record = asRecord(value);
    if (!record) {
        return;
    }
    pushChangedFile(target, seen, record.path);
    pushChangedFile(target, seen, record.filePath);
    pushChangedFile(target, seen, record.relativePath);
    pushChangedFile(target, seen, record.filename);
    pushChangedFile(target, seen, record.newPath);
    pushChangedFile(target, seen, record.oldPath);
    for (const nestedKey of [
        "item",
        "result",
        "input",
        "data",
        "changes",
        "files",
        "edits",
        "patch",
        "patches",
        "operations",
    ]) {
        if (!(nestedKey in record)) {
            continue;
        }
        collectChangedFiles(record[nestedKey], target, seen, depth + 1);
        if (target.length >= 12) {
            return;
        }
    }
}
function extractChangedFiles(payload) {
    const changedFiles = [];
    const seen = new Set();
    collectChangedFiles(asRecord(payload?.data), changedFiles, seen, 0);
    return changedFiles;
}
export function compareActivitiesByOrder(left, right) {
    if (left.sequence !== undefined && right.sequence !== undefined) {
        if (left.sequence !== right.sequence) {
            return left.sequence - right.sequence;
        }
    }
    else if (left.sequence !== undefined) {
        return 1;
    }
    else if (right.sequence !== undefined) {
        return -1;
    }
    const createdAtComparison = left.createdAt.localeCompare(right.createdAt);
    if (createdAtComparison !== 0) {
        return createdAtComparison;
    }
    const lifecycleRankComparison = compareActivityLifecycleRank(left.kind) - compareActivityLifecycleRank(right.kind);
    if (lifecycleRankComparison !== 0) {
        return lifecycleRankComparison;
    }
    return left.id.localeCompare(right.id);
}
function compareActivityLifecycleRank(kind) {
    if (kind.endsWith(".started") || kind === "tool.started") {
        return 0;
    }
    if (kind.endsWith(".progress") || kind.endsWith(".updated")) {
        return 1;
    }
    if (kind.endsWith(".completed") || kind.endsWith(".resolved")) {
        return 2;
    }
    return 1;
}
